import { useState } from 'react';
import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { FormatSelector } from '../../components/products/FormatSelector';
import { QuantityInput } from '../../components/products/QuantityInput';
import { route } from '../../utils/route';
import { getCSRFToken } from '../../utils/csrf';
import type { ProductFormat } from '../../types/products';

interface OriginalProduct {
    id: number;
    title: string;
    image_url: string;
    format_name: string | null;
    quantity: number;
    category: string | null;
}

interface Alternative {
    id: number;
    slug: string;
    title: string;
    image_url: string;
    category: string | null;
    formats: ProductFormat[];
}

interface UnavailableItem {
    original: OriginalProduct;
    alternatives: Alternative[];
}

interface DroppedItem {
    id: number;
    title: string;
    image_url: string;
    format_name: string | null;
    quantity: number;
}

interface Selection {
    alternative_id: number | null;
    format_id: number;
    quantity: number;
}

interface Props {
    orderId: number;
    availableCount: number;
    unavailableWithAlternatives: UnavailableItem[];
    unavailableWithoutAlternatives: DroppedItem[];
}

function getDefaultFormat(alt: Alternative): ProductFormat {
    return alt.formats.find(f => f.is_default) || alt.formats[0];
}

export default function RepeatAlternatives({
    orderId,
    availableCount,
    unavailableWithAlternatives,
    unavailableWithoutAlternatives,
}: Props) {
    const [selections, setSelections] = useState<Record<number, Selection>>(() => {
        const initial: Record<number, Selection> = {};
        unavailableWithAlternatives.forEach(item => {
            const firstAlt = item.alternatives[0];
            const defaultFmt = getDefaultFormat(firstAlt);
            initial[item.original.id] = {
                alternative_id: firstAlt.id,
                format_id: defaultFmt.id,
                quantity: item.original.quantity,
            };
        });
        return initial;
    });

    const [submitting, setSubmitting] = useState(false);

    const selectAlternative = (originalId: number, alt: Alternative | null) => {
        setSelections(prev => ({
            ...prev,
            [originalId]: alt
                ? {
                    alternative_id: alt.id,
                    format_id: getDefaultFormat(alt).id,
                    quantity: prev[originalId]?.quantity ?? 1,
                }
                : { alternative_id: null, format_id: 0, quantity: 0 },
        }));
    };

    const selectFormat = (originalId: number, formatId: number) => {
        setSelections(prev => ({
            ...prev,
            [originalId]: { ...prev[originalId], format_id: formatId },
        }));
    };

    const changeQuantity = (originalId: number, quantity: number) => {
        setSelections(prev => ({
            ...prev,
            [originalId]: { ...prev[originalId], quantity },
        }));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const res = await fetch(
                route('orders.repeat_alternatives.accept', { id: orderId }),
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': getCSRFToken(),
                    },
                    body: JSON.stringify({ selections: Object.values(selections) }),
                },
            );
            if (res.redirected) {
                window.location.href = res.url;
            } else {
                window.location.href = '/carrito';
            }
        } catch {
            setSubmitting(false);
        }
    };

    return (
        <MainLayout>
            <Head title="Revisá tu pedido">
                <meta name="robots" content="noindex" />
            </Head>

            <div className="mx-auto max-w-3xl px-4 py-8">
                <PageHeader title="Revisá tu pedido" subtitle="Algunos productos de tu pedido anterior ya no están disponibles" />

                {/* Available items summary */}
                {availableCount > 0 && (
                    <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                        <div className="flex items-center gap-2 text-green-800">
                            <i className="fa fa-check-circle" />
                            <span className="font-medium">
                                {availableCount} {availableCount === 1 ? 'producto se agrega' : 'productos se agregan'} sin cambios
                            </span>
                        </div>
                    </div>
                )}

                {/* Dropped items (no alternatives) */}
                {unavailableWithoutAlternatives.length > 0 && (
                    <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                        <div className="flex items-center gap-2 text-amber-800">
                            <i className="fa fa-exclamation-triangle" />
                            <span className="font-medium">
                                {unavailableWithoutAlternatives.length}{' '}
                                {unavailableWithoutAlternatives.length === 1
                                    ? 'producto ya no está disponible'
                                    : 'productos ya no están disponibles'}
                            </span>
                        </div>
                        <ul className="mt-2 space-y-1 pl-6 text-sm text-amber-700">
                            {unavailableWithoutAlternatives.map(item => (
                                <li key={item.id}>
                                    {item.title}
                                    {item.format_name && <span className="text-amber-600"> — {item.format_name}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Items with alternatives */}
                {unavailableWithAlternatives.length > 0 && (
                    <div className="space-y-6">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Elegí reemplazos para {unavailableWithAlternatives.length === 1 ? 'este producto' : 'estos productos'}
                        </h2>

                        {unavailableWithAlternatives.map(item => (
                            <AlternativeCard
                                key={item.original.id}
                                item={item}
                                selection={selections[item.original.id]}
                                onSelectAlternative={(alt) => selectAlternative(item.original.id, alt)}
                                onSelectFormat={(fmtId) => selectFormat(item.original.id, fmtId)}
                                onChangeQuantity={(qty) => changeQuantity(item.original.id, qty)}
                            />
                        ))}
                    </div>
                )}

                {/* Submit button */}
                <div className="mt-8 flex justify-end">
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="rounded-lg bg-green-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-green-700 disabled:opacity-50"
                    >
                        {submitting ? 'Cargando...' : 'Continuar al carrito'}
                    </button>
                </div>
            </div>
        </MainLayout>
    );
}

function AlternativeCard({
    item,
    selection,
    onSelectAlternative,
    onSelectFormat,
    onChangeQuantity,
}: {
    item: UnavailableItem;
    selection: Selection;
    onSelectAlternative: (alt: Alternative | null) => void;
    onSelectFormat: (formatId: number) => void;
    onChangeQuantity: (quantity: number) => void;
}) {
    const isSkipped = selection?.alternative_id === null;

    return (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            {/* Original product — top section with red accent */}
            <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                <div className="flex items-start gap-3">
                    <img
                        src={item.original.image_url}
                        alt={item.original.title}
                        className="h-12 w-12 shrink-0 rounded-lg object-cover opacity-50 grayscale"
                    />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-500 line-through">{item.original.title}</span>
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                                No disponible
                            </span>
                        </div>
                        <p className="mt-0.5 text-sm text-gray-400">
                            {item.original.format_name && <>{item.original.format_name} · </>}
                            Cant. {item.original.quantity}
                        </p>
                    </div>
                </div>
            </div>

            {/* Alternatives — bottom section */}
            <div className="p-4">
                <p className="mb-3 text-sm font-medium text-gray-600">Reemplazar con:</p>

                <div className="space-y-3">
                    {item.alternatives.map(alt => {
                        const isSelected = selection?.alternative_id === alt.id;

                        return (
                            <div
                                key={alt.id}
                                onClick={() => onSelectAlternative(alt)}
                                className={`cursor-pointer rounded-xl border-2 transition-all ${
                                    isSelected
                                        ? 'border-green-500 bg-green-50/50'
                                        : 'border-transparent bg-gray-50 hover:border-gray-200'
                                }`}
                            >
                                <div className="flex gap-3 p-3">
                                    <Radio checked={isSelected} />
                                    <img
                                        src={alt.image_url}
                                        alt={alt.title}
                                        className="h-[7.5rem] w-[7.5rem] shrink-0 rounded-lg object-cover"
                                    />
                                    <div className="min-w-0 flex-1 space-y-2">
                                        <p className={`font-medium ${isSelected ? 'text-green-900' : 'text-gray-900'}`}>
                                            {alt.title}
                                        </p>

                                        {isSelected && (
                                            <div className="mt-2 w-[14rem] space-y-2">
                                                {alt.formats.length > 0 && (
                                                    <div onClick={e => e.stopPropagation()}>
                                                        <FormatSelector
                                                            formats={alt.formats}
                                                            selectedFormatId={selection.format_id}
                                                            onChange={(f) => onSelectFormat(f.id)}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                                    <QuantityInput
                                                        value={selection.quantity}
                                                        onChange={onChangeQuantity}
                                                        min={1}
                                                    />
                                                    <Link
                                                        href={route('product.show', { slug: alt.slug })}
                                                        className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-center text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors whitespace-nowrap"
                                                    >
                                                        Ver producto
                                                    </Link>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Skip option */}
                    <div
                        onClick={() => onSelectAlternative(null)}
                        className={`cursor-pointer rounded-xl border-2 p-3 transition-all ${
                            isSkipped
                                ? 'border-gray-300 bg-gray-100'
                                : 'border-transparent bg-gray-50 hover:border-gray-200'
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            <Radio checked={isSkipped} muted />
                            <span className="text-sm text-gray-500">Omitir este producto</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Radio({ checked, muted }: { checked: boolean; muted?: boolean }) {
    const activeColor = muted ? 'border-gray-400 bg-gray-400' : 'border-green-500 bg-green-500';
    return (
        <div className={`hidden h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
            checked ? activeColor : 'border-gray-300'
        }`}>
            {checked && (
                <div className="h-2 w-2 rounded-full bg-white" />
            )}
        </div>
    );
}
